import { Component, computed, inject, input, model } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { TemplateInput } from '@core/filters/path-filter/templates';
import {
  RecipeParameterFieldErrorCode,
  RecipeParameterFilterContext,
  RecipeParameterKind,
  RecipeParameterProperty,
  RecipeParameterSchema,
  RecipeParameterValues
} from '@core/models/recipe-parameters.model';
import { PathFilterVocabularyService } from '@core/services/path-filter-vocabulary/path-filter-vocabulary.service';
import {
  datetimeLocalToIso,
  isoToDatetimeLocal,
  minItemsFor,
  orderedParameterNames,
  pathFilterHop,
  resolveParameterHelp,
  resolveParameterLabel,
  slotPickerFor,
  validateValues,
  widgetFor
} from '@core/utils/recipe-parameter-schema.utils';

import { MultiAutocompleteComponent } from '@shared/components/autocomplete/multi-autocomplete.component';
import {
  TemplateValue,
  TemplateValueInputComponent
} from '@shared/components/path-filter/template-value-input/template-value-input.component';

interface RecipeParameterField {
  name: string;
  widget: RecipeParameterKind;
  label: string;
  help?: string;
  minItems: number;
  contexts: RecipeParameterFilterContext[];
  /** Set when a slot the value fills refers to an entity or serves closed values. */
  picker: TemplateInput | null;
}

/**
 * Renders the form a recipe needs at plan creation, from the JSON Schema the
 * data layer derives per recipe. It knows nothing about recipes, plans or HTTP;
 * the current dataspace's catalogue tells it which values pick an entity or a
 * closed value instead of being typed.
 *
 * Values are held in wire format — an ISO 8601 instant for `date-time`, not the
 * local wall-clock string its input displays — so the parent can submit them
 * without further conversion.
 */
@Component({
  selector: 'app-recipe-parameter-form',
  imports: [
    FormsModule,
    TranslatePipe,
    MultiAutocompleteComponent,
    TemplateValueInputComponent
  ],
  templateUrl: './recipe-parameter-form.component.html',
  styles: ``
})
export class RecipeParameterFormComponent {
  private readonly translate = inject(TranslateService);
  private readonly vocabularies = inject(PathFilterVocabularyService);

  schema = input<RecipeParameterSchema | null>(null);
  values = model<RecipeParameterValues>({});

  /** Field-level messages from a rejected submission, keyed by parameter name. */
  serverErrors = input<Record<string, string>>({});

  /** Set once the planner has attempted to submit, so errors are not premature. */
  showErrors = input<boolean>(false);

  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  protected fields = computed<RecipeParameterField[]>(() => {
    this.currentLang();
    const schema = this.schema();
    const properties = schema?.properties ?? {};
    const instant = (key: string): string => this.translate.instant(key);

    return orderedParameterNames(schema).map((name) => {
      const property: RecipeParameterProperty | undefined = properties[name];
      const widget = widgetFor(property);
      const label = resolveParameterLabel(name, property, instant);
      const contexts = property?.['x-filter-context'] ?? [];
      const picker = slotPickerFor(contexts, (type) =>
        this.vocabularies.vocabularyOf(type)
      );
      return {
        name,
        widget,
        label,
        help: resolveParameterHelp(name, instant),
        minItems: minItemsFor(property),
        contexts,
        picker: picker && { name, label, kind: widget, ...picker }
      };
    });
  });

  protected errors = computed<Record<string, RecipeParameterFieldErrorCode>>(() =>
    validateValues(this.schema(), this.values())
  );

  protected scalarValue(name: string): string {
    const value = this.values()[name];
    return typeof value === 'string' ? value : '';
  }

  protected dateTimeInputValue(name: string): string {
    return isoToDatetimeLocal(this.scalarValue(name));
  }

  protected listValue(name: string): string[] {
    const value = this.values()[name];
    return Array.isArray(value) ? value : [];
  }

  protected setScalar(name: string, value: string): void {
    this.values.update((current) => ({ ...current, [name]: value }));
  }

  protected setValue(name: string, value: TemplateValue): void {
    this.values.update((current) => ({ ...current, [name]: value }));
  }

  protected setDateTime(name: string, localValue: string): void {
    this.setScalar(name, datetimeLocalToIso(localValue));
  }

  protected setList(name: string, value: unknown): void {
    const items = Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string')
      : [];
    this.values.update((current) => ({ ...current, [name]: items }));
  }

  /** The local error message for a field, or the data layer's if it rejected one. */
  protected errorFor(name: string): string | undefined {
    const serverError = this.serverErrors()[name];
    if (serverError !== undefined) {
      return serverError.length > 0
        ? serverError
        : this.translate.instant('recipe_parameters.errors.invalid');
    }
    if (!this.showErrors()) return undefined;

    const code = this.errors()[name];
    if (code === undefined) return undefined;
    if (code === 'min-items') {
      return this.translate.instant('recipe_parameters.errors.min_items', {
        count: this.fields().find((f) => f.name === name)?.minItems ?? 1
      });
    }
    return this.translate.instant(
      code === 'required'
        ? 'recipe_parameters.errors.required'
        : 'recipe_parameters.errors.invalid'
    );
  }

  /**
   * A one-line summary of what a value filters, so the planner understands what
   * it does. A parameter may feed several slots, so all are listed.
   */
  protected contextHint(contexts: RecipeParameterFilterContext[]): string | undefined {
    if (contexts.length === 0) return undefined;

    const parts = contexts.map((context) => {
      if (context.slot !== undefined && context.entityType === undefined) {
        return this.translate.instant('recipe_parameters.context.recipe_slot', {
          slot: context.slot
        });
      }
      const hop = context.grammar === 'pathFilter' ? pathFilterHop(context.field) : null;
      if (hop) {
        return this.translate.instant('recipe_parameters.context.entity_edge_field', {
          entity: context.entityType ?? '?',
          edge: hop.edge,
          field: hop.leaf
        });
      }
      return this.translate.instant('recipe_parameters.context.entity_field', {
        entity: context.entityType ?? '?',
        field: context.labelKey ?? context.field ?? '?'
      });
    });

    return [...new Set(parts)].join(' · ');
  }
}
